import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  /** Optional fallback override — receives the error and a reset fn. */
  fallback?: (args: { error: Error; reset: () => void }) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary.
 *
 * Catches any uncaught render-time error from descendants — including failed
 * Supabase queries, Razorpay/Paddle script loads, missing images that throw,
 * etc. — and renders a friendly card with retry instead of a white screen.
 *
 * Async errors thrown inside event handlers / promises are NOT caught here
 * (React's design); those should still surface via `toast.error(...)`. This
 * boundary is the safety net for everything else.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface in console so devs can diagnose; production observability can
    // hook in here later (Sentry, Logflare, etc.).
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback && this.state.error) {
      return this.props.fallback({ error: this.state.error, reset: this.reset });
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-5 py-10">
        <div className="w-full max-w-md rounded-3xl border border-destructive/30 bg-card p-6 text-center shadow-elevated sm:p-8">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-destructive/15 text-destructive">
            <AlertTriangle className="h-7 w-7" />
          </div>
          <h1 className="font-display text-xl font-bold uppercase tracking-tight text-foreground sm:text-2xl">
            Something broke
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            We hit an unexpected error. Your data is safe — try reloading the page.
          </p>
          {this.state.error?.message && (
            <p className="mt-3 break-words rounded-xl border border-border bg-muted/40 p-3 text-left text-[11px] font-mono text-muted-foreground">
              {this.state.error.message.slice(0, 240)}
            </p>
          )}
          <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Button
              variant="outline"
              onClick={() => {
                this.reset();
                window.location.assign("/");
              }}
              className="h-11"
            >
              <Home className="mr-2 h-4 w-4" />
              Go home
            </Button>
            <Button
              onClick={() => {
                this.reset();
                window.location.reload();
              }}
              className="h-11"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reload page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
